import React, { useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

type QuizSummary = { quizId: number; title: string; chapter: string };
type StudentQuizScore = {
  quizId: number;
  score: number | null;
  completed: boolean;
};
type BatchStudentQuizSummary = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  results: StudentQuizScore[];
};
type BatchQuizSummaryResponse = {
  quizzes: QuizSummary[];
  students: BatchStudentQuizSummary[];
};
type Batch = {
  id: number;
  name: string;
};

const AdminBatchQuizSummary: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [quizSummary, setQuizSummary] =
    useState<BatchQuizSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch batches on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/quizzes/mentor/active-batches`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setBatches(data || []));
  }, []);

  // Fetch quiz summary when batch changes
  useEffect(() => {
    if (!selectedBatchId) {
      setQuizSummary(null);
      return;
    }
    setLoading(true);
    fetch(
      `${API_BASE}/api/quizzes/mentor/batches/${selectedBatchId}/quiz-summary`,
      {
        credentials: "include",
      }
    )
      .then((res) => res.json())
      .then((data) => setQuizSummary(data))
      .catch((e) => {
        setQuizSummary(null);
        console.error(e);
      })
      .finally(() => setLoading(false));
  }, [selectedBatchId]);

  return (
    <div className="space-y-6">
      <div>
        <label className="font-bold mr-3">Select Batch:</label>
        <select
          className="border px-4 py-2 rounded"
          onChange={(e) => setSelectedBatchId(Number(e.target.value))}
          value={selectedBatchId ?? ""}
        >
          <option value="">-- Select Batch --</option>
          {batches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      {loading && <p className="text-[#009fe3]">Loading...</p>}

      {!loading && selectedBatchId && quizSummary && (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse border font-sans text-sm">
            <thead>
              <tr className="bg-[#009fe3] text-white">
                <th className="border px-4 py-2">Student</th>
                {quizSummary.quizzes.map((q) => (
                  <th key={q.quizId} className="border px-4 py-2">
                    {q.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {quizSummary.students.map((student) => (
                <tr key={student.id} className="hover:bg-blue-50">
                  <td className="border px-4 py-2 font-semibold">
                    {student.firstName} {student.lastName}
                  </td>
                  {student.results.map((r) => (
                    <td key={r.quizId} className="border px-4 py-2 text-center">
                      {r.completed && r.score !== null ? (
                        r.score
                      ) : (
                        <span className="text-red-500 font-medium">x</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && selectedBatchId && !quizSummary && (
        <p className="text-red-500">
          No quiz summary available for this batch.
        </p>
      )}
    </div>
  );
};

export default AdminBatchQuizSummary;
